import { useCallback, useMemo, useState } from 'react';
import { apiGetLanguagesAbled } from '@/services/api';

export interface GlobalLanguageState {
  languages: API.LanguageItem[];
  currentLanguageCode?: string;
  initialized: boolean;
}

const getDefaultLanguageCode = (
  languages: API.LanguageItem[],
): string | undefined =>
  languages.find((item) => item.isDefault)?.code || languages[0]?.code;

export default function useGlobalLanguageModel() {
  const [state, setState] = useState<GlobalLanguageState>({
    languages: [],
    currentLanguageCode: undefined,
    initialized: false,
  });

  const fetchLanguages = useCallback(async () => {
    try {
      const response = await apiGetLanguagesAbled();
      const languageList = response?.data ?? [];
      setState((prev) => {
        const nextCode =
          prev.currentLanguageCode &&
          languageList.some((item) => item.code === prev.currentLanguageCode)
            ? prev.currentLanguageCode
            : getDefaultLanguageCode(languageList);
        return {
          ...prev,
          languages: languageList,
          currentLanguageCode: nextCode,
        };
      });
    } finally {
      setState((prev) => ({ ...prev, initialized: true }));
    }
  }, []);

  const setLanguageByCode = useCallback((code: string) => {
    setState((prev) => {
      if (!prev.languages.some((item) => item.code === code)) {
        return prev;
      }
      return {
        ...prev,
        currentLanguageCode: code,
      };
    });
  }, []);

  return useMemo(
    () => ({
      ...state,
      fetchLanguages,
      setLanguageByCode,
    }),
    [fetchLanguages, setLanguageByCode, state],
  );
}
